import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KeyRound, Plus, Sparkles } from 'lucide-react';
import type {
  CreateModelProviderSecretRequest,
  UpdateModelProviderSecretRequest,
} from '@reflex/shared';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@reflex/ui/components/ui/card';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { ModelProviderSecretsManager } from '@/components/secrets/ModelProviderSecretsManager';
import { OrgModelProviderSecretDeleteBody } from '@/components/secrets/OrgModelProviderSecretDeleteBody';
import { LlmTaskAssignmentsManager } from '@/components/llm-tasks/LlmTaskAssignmentsManager';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useVisibleModelProviders } from '@/hooks/config/useVisibleModelProviders';
import { useAgentModelSupport } from '@/hooks/config/useAgentModelSupport';

/**
 * Org-tier Model Providers page (`/{orgSlug}/org/model-providers`).
 *
 * Org keys are stored directly in `model_provider_secrets` (`mps_*`)
 * through the org-tier endpoints — the same multi-key surface the user
 * and team tiers use. Each provider can hold multiple named keys, and
 * the launching agent picks one (or falls back through the user → team
 * → org precedence chain).
 */
export function OrgModelProvidersPage({ embedded = false }: { embedded?: boolean }) {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const orgKey = useOrgKey();
  const routes = useOrgRoutes();
  const { can } = useAuth();
  const canManage = can('secrets:manage');
  const queryClient = useQueryClient();
  const [addKeyOpen, setAddKeyOpen] = useState(false);

  const mpsKey = queryKeys.orgModelProviderSecrets(orgKey);
  const mpsQuery = useQuery({
    queryKey: mpsKey,
    queryFn: () => api.listOrgModelProviderSecrets(orgId),
    enabled: !!orgId && canManage,
  });
  const visibleProviders = useVisibleModelProviders(mpsQuery.data ?? []);
  const modelSupport = useAgentModelSupport();
  const runloopAvailable = Object.values(modelSupport.data?.agents ?? {}).some(
    (entry) =>
      entry.status === 'available' && entry.providers.some((provider) => provider.id === 'runloop'),
  );

  // Create/update failures are shown inline in the manager dialog
  // (which awaits the mutation and stays open on rejection), so only the
  // success path notifies via toast.
  const createMutation = useMutation({
    mutationFn: (data: CreateModelProviderSecretRequest) =>
      api.createOrgModelProviderSecret(orgId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Organization model provider key saved');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      secretId,
      data,
    }: {
      secretId: string;
      data: UpdateModelProviderSecretRequest;
    }) => api.updateOrgModelProviderSecret(orgId, secretId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Organization model provider key updated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (secretId: string) => api.deleteOrgModelProviderSecret(orgId, secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Organization model provider key deleted');
    },
    onError: (err: Error) =>
      toast.error('Failed to delete organization model provider key', { description: err.message }),
  });

  const isPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const body = (
    <div className={embedded ? 'space-y-8' : 'mt-6 space-y-8'}>
      {!embedded && (
        <PageHeader
          iconVariant="box"
          icon={<Sparkles className="h-5 w-5 text-muted-foreground" />}
          title="Organization Model Providers"
          description="API keys used by agents launched in this organization that lack a personal or team-tier key for the provider. Each provider can hold multiple named keys."
          docs="administration/security#secrets-tiers"
          actions={
            <div className="flex items-center gap-2">
              {canManage ? (
                <Button
                  aria-label="Add key"
                  disabled={isPending}
                  onClick={() => setAddKeyOpen(true)}
                >
                  <Plus />
                  <span className="sm:hidden">Add</span>
                  <span className="hidden sm:inline">Add key</span>
                </Button>
              ) : null}
              <Button asChild variant="outline" size="sm">
                <Link
                  to={routes.credentialsHubSection('organization', 'secrets')}
                  aria-label="All secrets"
                >
                  <KeyRound className="h-3.5 w-3.5" /> All secrets
                </Link>
              </Button>
            </div>
          }
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Runloop Models</CardTitle>
          <CardDescription>
            Deployment-managed models provided by Runloop. No organization API key is required.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <span className="text-sm text-muted-foreground">
            {modelSupport.isLoading
              ? 'Checking availability…'
              : runloopAvailable
                ? 'Available · GLM-5.2'
                : 'Not available on this deployment'}
          </span>
        </CardContent>
      </Card>

      <ModelProviderSecretsManager
        title="Organization API Keys"
        description={
          canManage
            ? 'Add organization-wide model-provider keys here. Used by agents launched in this organization when neither the launcher nor their team has a key for the provider.'
            : 'You do not have permission to manage organization model providers.'
        }
        secrets={mpsQuery.data ?? []}
        providers={visibleProviders}
        isLoading={mpsQuery.isLoading}
        isPending={isPending}
        readOnly={!canManage}
        showSectionAction={embedded}
        addOpen={addKeyOpen}
        onAddOpenChange={setAddKeyOpen}
        emptyState={
          canManage
            ? 'No organization model-provider keys yet. Add one to share credentials across the organization.'
            : 'No organization model-provider keys configured.'
        }
        onCreate={(provider, name, value, _scopeChoice, baseUrl) =>
          createMutation.mutateAsync({ provider, type: 'apiKey', name, value, baseUrl })
        }
        onUpdate={(secretId, patch) => updateMutation.mutateAsync({ secretId, data: patch })}
        onDelete={(secretId) => deleteMutation.mutate(secretId)}
        renderDeleteDescription={
          orgId
            ? (secret) => (
                <OrgModelProviderSecretDeleteBody
                  orgId={orgId}
                  secretId={secret.id}
                  name={secret.name}
                />
              )
            : undefined
        }
      />

      {canManage && orgId && <LlmTaskAssignmentsManager orgId={orgId} />}
    </div>
  );

  if (embedded) return body;

  return <PageLayout maxWidth="4xl">{body}</PageLayout>;
}
